import React from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Collapse,
  Button,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import TuneOutlinedIcon from "@material-ui/icons/TuneOutlined";
import StorefrontOutlinedIcon from "@material-ui/icons/StorefrontOutlined";

import SecurityOutlinedIcon from "@material-ui/icons/SecurityOutlined";
import PersonOutlineOutlinedIcon from "@material-ui/icons/PersonOutlineOutlined";
import ProgramLogDetailView from "./ProgramLogDetailView";
import { useTimezone } from "../../Context/TimezoneContext";
import {
  ACTION_STYLES,
  MODULE_CONFIGS,
  ROLE_BADGE_STYLES,
  actionLabel,
  formatReference,
  formatRole,
  formatDateTime,
  formatRelativeTime,
  getInitials,
  formatClientIp,
} from "./ProgramLogConstants";

const useStyles = makeStyles((theme) => ({
  cardList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  logCard: {
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    padding: theme.spacing(1.5),
    boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
    transition: "all 0.2s ease",
    "&:hover": {
      boxShadow: "0 3px 8px rgba(0,0,0,0.06)",
      borderColor: "#cbd5e1",
    },
  },
  topRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  moduleBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.75rem",
    fontWeight: 700,
    padding: "3px 8px",
    borderRadius: 6,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  timeText: {
    fontSize: "0.75rem",
    color: "#64748b",
    fontWeight: 500,
  },
  midRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.25),
  },
  referenceWrapper: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  referenceCode: {
    fontWeight: 700,
    fontSize: "0.9375rem",
    color: "#0f172a",
    fontFamily: "inherit",
  },
  referenceBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "3px 9px",
    borderRadius: 6,
    border: "1px solid #e2e8f0",
    fontSize: "0.875rem",
    lineHeight: 1.25,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.02)",
  },
  referencePrefix: {
    fontWeight: 800,
    fontSize: "0.78rem",
    letterSpacing: "0.04em",
  },
  referenceSeparator: {
    fontWeight: 700,
    opacity: 0.6,
    fontSize: "0.78rem",
  },
  referenceNumber: {
    fontWeight: 700,
    color: "#0f172a",
    letterSpacing: "-0.01em",
  },
  referenceBulkBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 8px",
    borderRadius: 6,
    border: "1px solid",
    fontSize: "0.78rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  userRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 0),
    borderTop: "1px solid #f1f5f9",
    borderBottom: "1px solid #f1f5f9",
    marginBottom: theme.spacing(1),
  },
  userAvatar: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    backgroundColor: "#e2e8f0",
    color: "#334155",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.75rem",
    fontWeight: 700,
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  userNameWrapper: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  userName: {
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: "#1e293b",
  },
  userEmail: {
    fontSize: "0.7rem",
    color: "#64748b",
  },
  toggleButton: {
    fontSize: "0.75rem",
    textTransform: "none",
    fontWeight: 600,
    color: "#2563eb",
    padding: theme.spacing(0.5, 1),
    borderRadius: 6,
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    backgroundColor: "#f8fafc",
    "&:hover": {
      backgroundColor: "#f1f5f9",
    },
  },
}));

export default function ProgramLogCardList({
  logs = [],
  expandedRows = {},
  onToggleRow,
  currencySymbol = "$",
}) {
  const classes = useStyles();
  const { timezone } = useTimezone();

  const getModuleIcon = (mod) => {
    switch (mod) {
      case "Invoice":
        return <ReceiptOutlinedIcon style={{ fontSize: 16 }} />;
      case "StockAdjustment":
        return <TuneOutlinedIcon style={{ fontSize: 16 }} />;
      case "Item":
        return <StorefrontOutlinedIcon style={{ fontSize: 16 }} />;
      default:
        return null;
    }
  };

  return (
    <div className={classes.cardList}>
      {logs.map((log) => {
        const isExpanded = expandedRows[log._id] === true;
        const hasDetail =
          (Array.isArray(log.items) && log.items.length > 0) ||
          (log.details && Object.keys(log.details).length > 0);

        const modConfig = MODULE_CONFIGS[log.module] || {
          label: log.module,
          color: "#334155",
          bgColor: "#f1f5f9",
          borderColor: "#cbd5e1",
        };

        const ref = formatReference(log);

        return (
          <Paper key={log._id} elevation={0} className={classes.logCard}>
            {/* Top Row: Module Badge + Action Badge + Relative Timestamp */}
            <div className={classes.topRow}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span
                  className={classes.moduleBadge}
                  style={{
                    backgroundColor: modConfig.bgColor,
                    color: modConfig.color,
                    border: `1px solid ${modConfig.borderColor}`,
                  }}
                >
                  {getModuleIcon(log.module)}
                  {modConfig.label}
                </span>

                <Chip
                  size="small"
                  label={actionLabel(log.action)}
                  style={{
                    fontSize: "0.7rem",
                    height: 22,
                    fontWeight: 600,
                    ...(ACTION_STYLES[log.action] || {}),
                  }}
                />
              </div>

              <Tooltip title={formatDateTime(log.createdAt, timezone)}>
                <span className={classes.timeText}>
                  {formatRelativeTime(log.createdAt, timezone)}
                </span>
              </Tooltip>
            </div>

            {/* Mid Row: Reference ID */}
            <div className={classes.midRow}>
              <div className={classes.referenceWrapper}>
                {ref.isBulk ? (
                  <span
                    className={classes.referenceBulkBadge}
                    style={{
                      color: ref.color,
                      backgroundColor: ref.bgColor,
                      borderColor: ref.borderColor,
                    }}
                  >
                    {ref.number}
                  </span>
                ) : ref.prefix ? (
                  <span
                    className={classes.referenceBadge}
                    style={{
                      backgroundColor: ref.bgColor,
                      borderColor: ref.borderColor,
                    }}
                  >
                    <span className={classes.referencePrefix} style={{ color: ref.color }}>
                      {ref.prefix}
                    </span>
                    <span className={classes.referenceSeparator} style={{ color: ref.color }}>
                      -
                    </span>
                    <span className={classes.referenceNumber}>
                      {ref.number}
                    </span>
                  </span>
                ) : (
                  <span className={classes.referenceCode}>{ref.number}</span>
                )}
              </div>

              {/* Exact timestamp */}
              <Typography variant="caption" style={{ color: "#94a3b8" }}>
                {new Date(log.createdAt).toLocaleTimeString(undefined, {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Typography>
            </div>

            {/* User Row: Avatar + Name + Role Chip */}
            <div className={classes.userRow}>
              <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                <div className={classes.userAvatar}>
                  {getInitials(log.userName || log.userEmail)}
                </div>
                <div className={classes.userNameWrapper}>
                  <span className={classes.userName}>
                    {log.userName || "System User"}
                  </span>
                  {log.userEmail && (
                    <span className={classes.userEmail}>{log.userEmail}</span>
                  )}
                  {log.details?.ipAddress && (
                    <span className={classes.userEmail}>IP: {formatClientIp(log.details.ipAddress)}</span>
                  )}
                </div>
              </div>

              {(() => {
                const { label, tier } = formatRole(log.userRole);
                const badgeStyle = ROLE_BADGE_STYLES[tier];
                return (
                  <Chip
                    size="small"
                    icon={
                      tier === "user" ? (
                        <PersonOutlineOutlinedIcon style={{ fontSize: 13 }} />
                      ) : (
                        <SecurityOutlinedIcon style={{ fontSize: 13, color: badgeStyle.color }} />
                      )
                    }
                    label={label}
                    style={{
                      fontSize: "0.7rem",
                      height: 22,
                      fontWeight: 600,
                      ...badgeStyle,
                    }}
                  />
                );
              })()}
            </div>

            {/* Expand / Details Accordion */}
            {hasDetail && (
              <>
                <Button
                  className={classes.toggleButton}
                  onClick={() => onToggleRow(log._id)}
                  endIcon={
                    isExpanded ? (
                      <ExpandLessIcon style={{ fontSize: 18 }} />
                    ) : (
                      <ExpandMoreIcon style={{ fontSize: 18 }} />
                    )
                  }
                >
                  {isExpanded ? "Hide Audit Details" : "View Audit Details"}
                  {Array.isArray(log.items) && log.items.length > 0 && (
                    <span style={{ color: "#64748b", fontWeight: 400, marginLeft: 4 }}>
                      ({log.items.length} {log.items.length === 1 ? "item" : "items"})
                    </span>
                  )}
                </Button>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box mt={1}>
                    <ProgramLogDetailView log={log} currencySymbol={currencySymbol} />
                  </Box>
                </Collapse>
              </>
            )}
          </Paper>
        );
      })}
    </div>
  );
}
